import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { promptsAPI, categoriesAPI, tokenAnalyzerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaRocket, FaInfoCircle } from 'react-icons/fa';

const CreatePrompt = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tokenCount, setTokenCount] = useState(0);
  const [analyzingTokens, setAnalyzingTokens] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prompt_text: '',
    examples: '',
    category_id: '',
    tags: '',
    target_ai_model: 'gpt-4',
    is_public: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleQuillChange = (content) => {
    setFormData({
      ...formData,
      prompt_text: content,
    });
    // Debounce token counting
    if (content && content.length > 10) {
      debouncedTokenCount(content);
    }
  };

  // Debounce function for token counting
  let tokenCountTimeout;
  const debouncedTokenCount = (text) => {
    clearTimeout(tokenCountTimeout);
    tokenCountTimeout = setTimeout(() => {
      analyzeTokens(text);
    }, 1000);
  };

  const analyzeTokens = async (text) => {
    // Remove HTML tags for token counting
    const plainText = text.replace(/<[^>]*>/g, '');
    if (!plainText.trim()) {
      setTokenCount(0);
      return;
    }

    setAnalyzingTokens(true);
    try {
      const response = await tokenAnalyzerAPI.count(plainText, formData.target_ai_model);
      setTokenCount(response.data.tokenCount || 0);
    } catch (error) {
      console.error('Token count error:', error);
    } finally {
      setAnalyzingTokens(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    if (!formData.prompt_text.trim()) {
      toast.error('Please enter the prompt text');
      return;
    }
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setLoading(true);
    try {
      // Parse tags (comma-separated)
      const tagsArray = formData.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const promptData = {
        ...formData,
        tags: tagsArray,
        token_count: tokenCount,
      };

      const response = await promptsAPI.create(promptData);
      toast.success('Prompt created successfully!');
      navigate(`/prompts/${response.data.prompt.id}`);
    } catch (error) {
      console.error('Create prompt error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create prompt';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'code-block',
    'link',
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Create New Prompt</h1>
        <p className="text-gray-600">
          Share your AI prompt with the community and get valuable feedback
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
        {/* Title */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2" htmlFor="title">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter a catchy title for your prompt"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            maxLength={200}
            required
          />
          <p className="text-xs text-gray-500 mt-1">{formData.title.length}/200 characters</p>
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2" htmlFor="description">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Briefly describe what this prompt does and when to use it"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            rows={3}
            maxLength={500}
            required
          />
          <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
        </div>

        {/* Category and Target AI Model */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="category_id">
              Category *
            </label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
              required
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="target_ai_model">
              Target AI Model
            </label>
            <select
              id="target_ai_model"
              name="target_ai_model"
              value={formData.target_ai_model}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            >
              <optgroup label="OpenAI (GPT/O)">
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4o-mini">GPT-4o Mini</option>
                <option value="gpt-4.1">GPT-4.1</option>
                <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="o4-mini">o4-mini</option>
                <option value="o3">o3</option>
              </optgroup>
              <optgroup label="Anthropic Claude">
                <option value="claude-3.5-sonnet">Claude 3.5 Sonnet</option>
                <option value="claude-3.5-haiku">Claude 3.5 Haiku</option>
                <option value="claude-3-opus">Claude 3 Opus</option>
                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                <option value="claude-3-haiku">Claude 3 Haiku</option>
              </optgroup>
              <optgroup label="Google Gemini">
                <option value="gemini-pro">Gemini Pro</option>
                <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                <option value="gemini-1.5-pro-latest">Gemini 1.5 Pro (latest)</option>
                <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                <option value="gemini-1.5-flash-latest">Gemini 1.5 Flash (latest)</option>
              </optgroup>
              <optgroup label="Meta Llama">
                <option value="llama-2">Llama 2</option>
                <option value="llama-3">Llama 3</option>
                <option value="llama-3.1-8b-instruct">Llama 3.1 8B Instruct</option>
                <option value="llama-3.1-70b-instruct">Llama 3.1 70B Instruct</option>
                <option value="llama-3.2-3b-instruct">Llama 3.2 3B Instruct</option>
                <option value="llama-3.2-11b-instruct">Llama 3.2 11B Instruct</option>
              </optgroup>
              <optgroup label="Mistral AI">
                <option value="mistral">Mistral</option>
                <option value="mistral-large-latest">Mistral Large (latest)</option>
                <option value="mixtral-8x7b-instruct">Mixtral 8x7B Instruct</option>
                <option value="mixtral-8x22b-instruct">Mixtral 8x22B Instruct</option>
              </optgroup>
              <optgroup label="Cohere">
                <option value="command-r">Command R</option>
                <option value="command-r-plus">Command R+</option>
              </optgroup>
              <optgroup label="DeepSeek">
                <option value="deepseek-chat">DeepSeek Chat</option>
                <option value="deepseek-reasoner">DeepSeek Reasoner</option>
              </optgroup>
              <optgroup label="Other">
                <option value="palm-2">PaLM 2</option>
                <option value="other">Other / Multiple</option>
              </optgroup>
            </select>
          </div>
        </div>

        {/* Prompt Text with Token Counter */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-semibold">Prompt Text *</label>
            <div className="flex items-center gap-2">
              {analyzingTokens ? (
                <span className="text-xs text-gray-500">Counting tokens...</span>
              ) : (
                <span className="text-sm font-semibold text-boop-primary">
                  {tokenCount} tokens
                </span>
              )}
            </div>
          </div>
          <ReactQuill
            theme="snow"
            value={formData.prompt_text}
            onChange={handleQuillChange}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Write your prompt here. Use formatting to make it clear and easy to understand..."
            className="bg-white"
            style={{ height: '300px', marginBottom: '50px' }}
          />
        </div>

        {/* Examples */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2" htmlFor="examples">
            Examples (Optional)
          </label>
          <textarea
            id="examples"
            name="examples"
            value={formData.examples}
            onChange={handleChange}
            placeholder="Provide example inputs/outputs to help users understand how to use this prompt"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
            rows={4}
          />
        </div>

        {/* Tags */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-2" htmlFor="tags">
            Tags
          </label>
          <input
            type="text"
            id="tags"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="Enter tags separated by commas (e.g., creative, marketing, copywriting)"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            <FaInfoCircle className="inline mr-1" />
            Tags help others discover your prompt
          </p>
        </div>

        {/* Public Checkbox */}
        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="mr-2 w-4 h-4"
            />
            <span className="text-sm">
              Make this prompt public (others can view and rate it)
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-boop-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-boop-secondary transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              'Creating...'
            ) : (
              <>
                <FaRocket className="inline mr-2" />
                Create Prompt
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePrompt;
